import { useEffect, useRef, useState } from "react";
import { Form, ProgressBar, Spinner } from "react-bootstrap";
import { Draggable } from "react-drag-reorder";
import { Rating } from "@mui/material";
import StarIcon from '@mui/icons-material/Star';

import { sleep, uploadLargeAssetAndAssesmble } from "../../../utils/utils";
import { programmingLanguages, QUESTION_TYPE } from "../../../shared/utils/constants";
import ReactQuillEditor from "../../../shared/form/ReactQuilEditor";
import CodeIDE from "../../../shared/components/CodeIDE";
import { Trash } from "../../../shared/icons/Icons";
import { aiImageValidation } from "../../../shared/settings/apiConfig";
import { postApi } from "../../../shared/services/apiHelper";

import './QuestionRenderer.scss';

export const RenderQuestion = ({ question, questionIndex, inlineAttachment=false, attachments, value, onChange, assetCallback, deleteAssetCallback }) => {

    const [loadQuestion, setLoadQuestion] = useState(false);

    useEffect(() => {
        setLoadQuestion(false);
        if (question != null && typeof question === 'object' && question.id) {
            let answer = null, previousAnswer = null, language = null;
            if (question.questiontype == QUESTION_TYPE.MULTICHOICE || question.questiontype == QUESTION_TYPE.TRUEORFALSE) {
                let answerSet = [];
                question.options.map((item) => {
                    if (item.answered) {
                        answerSet.push(item.id);
                    }
                });

                answer = [...answerSet].sort();
                previousAnswer = [...answerSet].sort();
            } else {
                if (question.questiontype == QUESTION_TYPE.REORDER) {
                    answer = question.options;
                    previousAnswer = null;
                } else if (question.questiontype == QUESTION_TYPE.RATING) {
                    answer = question.ratinganswer;
                    previousAnswer = null;
                } else if (question.questiontype == QUESTION_TYPE.DESCRIPTIVE) {
                    answer = question.singleinputanswer;
                    previousAnswer = question.singleinputanswer;
                } else if (question.questiontype == QUESTION_TYPE.CODING) {
                    answer = question.singleinputanswer;
                    previousAnswer = question.singleinputanswer;
                    language = question.programminglanguageopted ? question.programminglanguageopted : question.programminglanguage == "Any language" ? programmingLanguages[0].id : question.programminglanguage;
                }
            }

            onChange && onChange({ answer, previousAnswer, language });

            setLoadQuestion(true);
        }
    }, [question.id]);

    const answerOnChange = (answer, language) => {
        onChange && onChange({ answer, previousAnswer: value.previousAnswer, language: language || value.language });
    };

    if (loadQuestion) {
        return (
            <div className="question-renderer p-3">
                {/** Question Title */}
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex gap-2">
                        <div>{(questionIndex + 1) + '.'}</div>
                        <div className="question-content" dangerouslySetInnerHTML={{ __html: question.questionname }}></div>
                    </div>
                    {
                        inlineAttachment ? 
                        <MediaAttachment
                            question={question} 
                            attachment={attachments}
                            attachmentCallback={(data, uploading) => {
                                // setUploadLoading(uploading);
                                // setAllowAttachmentData(data);

                                assetCallback && assetCallback(data);
                            }}
                        /> : null
                    }
                </div>


                {/** Question Assets */}
                {
                    Array.isArray(question.assets) && (question.assets.length > 0) && question.assets.map((asset) =>
                        <div className="d-flex align-items-center justify-content-center p-3">
                            <div>
                                {
                                    asset.type.startsWith("image") ? <img style={{ width: '20rem' }} src={asset.url} /> : null
                                }
                                {
                                    asset.type.startsWith("video") ?
                                        <video controls style={{ width: "100%", height: "250px" }}>
                                            <source src={asset.url} type={asset.type} />
                                        </video> : null
                                }
                                {
                                    asset.type == "application/pdf" ?
                                        <a href={asset.url} target="_blank" rel="noopener noreferrer">
                                            <img style={{ width: '10rem' }} src={`${process.env.PUBLIC_URL}/static/images/pdf-icon.png`} />
                                        </a> : ''
                                }
                            </div>
                        </div>
                    )
                }
                <div className="d-flex">
                    {/** For rendering answer */}
                    {renderAnswer(question, value.answer, value.language, answerOnChange)}
                </div>

                {/** For rendering uploaded assets */}
                {
                    question?.uploadedassets?.length > 0 || attachments.length > 0 ?
                        <div className="d-flex flex-column gap-3 p-3">
                            <div className="font-weight-bold">Uploaded Assets</div>
                            <div className="d-flex align-items-center gap-3">
                                {
                                    question?.uploadedassets?.length > 0 ? renderImageFromView(question.uploadedassets, deleteAssetCallback, true) : null
                                }
                                {
                                    attachments.length > 0 ? renderImageFromView(attachments, deleteAssetCallback, false) : null
                                }
                            </div>
                        </div> : null
                }

                {/* {
                    message.message && <div className={`p-3 text-${message.type == 'success' ? 'success' : 'danger'}`}>{message.message}</div>
                } */}
            </div>
        )
    }

    return null;
};

export const renderImageFromView = (assets, deleteAssetCallback, isUploaded=false, readOnly=false) => {
    return (
        assets.map((asset, index) =>
            <div key={"asset-" + index} className="position-relative asset">
                {
                    asset.type.startsWith("image") ?
                    <img style={{ width: '12rem' }} src={asset.url} /> : ''
                }
                {
                    asset.type.startsWith("video") ?
                    <video controls style={{ width: "100%", height: "250px" }}>
                        <source src={asset.url} type={asset.type} />
                    </video> : ''
                }
                {
                    asset.type == "application/pdf" ?
                    <a href={asset.url} target="_blank" rel="noopener noreferrer">
                        <img style={{ width: '10rem' }} src={`${process.env.PUBLIC_URL}/static/images/pdf-icon.png`} />
                    </a> : ''
                }
                {
                    readOnly ? null : 
                    (
                        asset.progress ?
                        <div className="asset-upload-backdrop">
                            {
                                asset.progress?.type === 'progress' ? 
                                <ProgressBar now={asset.progress.value} label={`${asset.progress.value}%`} /> : ''
                            }
                            {
                                asset.progress?.type === 'spinner' ? 
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                    <Spinner />
                                    <span>{asset.progress.message}</span>
                                </div> : ''
                            }
                        </div> :
                        <div className="asset-delete" role="button" onClick={() => deleteAssetCallback && deleteAssetCallback(asset, index, isUploaded)}>
                            <Trash size={16} />
                        </div>
                    )
                }
            </div>
        )
    )
};

export const MediaAttachment = ({ question, attachment, attachmentCallback }) => {

    const getDefaultImageData = (name, type, size, file, url) => {
        return { name, type, size, file, url };
    };

    const openMediaPicker = () => {
        let input = document.createElement('input');
        input.type = 'file';
        input.onchange = async (event) => {
            if (event.target.files?.length > 0) {
                let value = event.target.files[0];
                uploadMedia(value);
            }
        }
        input.click();
    };

    const uploadMedia = async (asset) => {
        try {
            // Initially making it as empty
            passAttachmentData([]);

            // For refreshing the state.
            await sleep(10);

            // Saving the image in state.
            let attachmentData = [getDefaultImageData(asset.name, asset.type, asset.size, asset, URL.createObjectURL(asset))];

            // Starting the upload process
            const url = await uploadLargeAssetAndAssesmble(
                attachmentData[0],
                (progressData) => {
                    // Upload Progress Callback
                    attachmentData[0].progress = getProgressData('progress', '', progressData.progress);
                    passAttachmentData([...attachmentData], progressData.uploading);
                }
            );

            // if (question.questiontype == QUESTION_TYPE.DESCRIPTIVE && attachmentData[0].type.includes('image')) {
                
            //     // Validating handdrawn image using AI
            //     attachmentData[0].progress = getProgressData('spinner', 'Validating Image...', null);
            //     attachmentData[0].serverurl = url;
            //     passAttachmentData(attachmentData, true);

            //     const payload = { input_image: url };
            //     postApi(aiImageValidation, { data: payload }).then(response => {
            //         if (response.data.data.status) {
            //             attachmentData[0].progress = null;
            //             passAttachmentData(attachmentData);
            //         } else {
            //             passAttachmentData([]);
            //             // setMessageWithTimeout('error', response?.data?.data?.message || 'Error while validating the image.');
            //         }
            //     }).catch(error => {
            //         passAttachmentData([]);
            //         // setMessageWithTimeout('error', 'Error while validating the image.');
            //     });
            // } else {
            //     attachmentData[0].progress = null;
            //     attachmentData[0].serverurl = url;
            //     passAttachmentData(attachmentData);
            // }

            attachmentData[0].progress = null;
            attachmentData[0].serverurl = url;
            passAttachmentData(attachmentData);
            
        } catch (error) {
            // setMessageWithTimeout('error', 'Error while uploading the asset...');
        }
    };

    const passAttachmentData = (attachmentData, uploading=false) => {
        attachmentCallback && attachmentCallback(attachmentData, uploading);
    };

    const getProgressData = (type, message, value) => {
        return { type, message, value }
    };

    if (question?.allowattachments || question?.questiontype == QUESTION_TYPE.GRAPHICAL_SUBMISSION) {
        return (
            <div role={"button"} className="d-flex flex-column align-items-center gap-1" 
                onClick={() => {
                    // For now we can upload only one image per question.
                    if (Array.isArray(question?.uploadedassets) && question.uploadedassets.length == 0 && attachment.length == 0) {
                        openMediaPicker();
                    }
                }}>
                <img style={{ width: "2rem" }} src={`${process.env.PUBLIC_URL}/static/images/attachment.png`} />
                <div className="img-text">Attach File</div>
            </div>
        )
    }

    return null;
};

const renderAnswer = (question, answer, language=null, onChange) => {
    switch (question.questiontype) {
        case QUESTION_TYPE.DESCRIPTIVE:
            return (
                <DescriptiveOption
                    answer={answer}
                    onChange={(value) => onChange(value)} />
            )
        case QUESTION_TYPE.MULTICHOICE:
            return (
                <MultichoiceOption
                    options={question.options}
                    answer={answer}
                    onChange={(value) => onChange(value)} />
            )
        case QUESTION_TYPE.TRUEORFALSE:
            return (
                <SingleChoiceOption
                    question={question}
                    options={question.options}
                    answer={answer}
                    onChange={(value) => onChange(value)} />
            )
        case QUESTION_TYPE.REORDER:
            return (
                <ReorderOption
                    options={question.options}
                    answer={answer}
                    onChange={(value) => onChange(value)} />
            )
        case QUESTION_TYPE.RATING:
            return (
                <RatingOption
                    answer={answer}
                    onChange={(value) => onChange(value)} />
            )
        case QUESTION_TYPE.CODING:
            return (
                <CodingOption
                    question={question}
                    answer={answer}
                    language={language}
                    onChange={(value, language) => onChange(value, language)} />
            )
        default:
            break;
    }
};

const DescriptiveOption = ({ answer, onChange }) => {

    const quillRef = useRef([]);

    return (
        <div className="flex-grow-1 p-2 p-sm-3 gap-3">
            <ReactQuillEditor
                reference={quillRef}
                keyItem={0}
                placeholder={"Answer"}
                value={answer}
                onChange={(value, delta, source) => {
                    if (source !== 'user') return;

                    let deltas = delta?.ops?.map(op => {
                        if (op.insert && typeof op.insert === 'string') {
                            return (op.insert)
                        }
                    })

                    deltas = deltas.join("");

                    if (deltas.length <= 1) {
                        onChange && onChange(value);
                    } else if (deltas != "") {
                        const quill = quillRef.current[0].getEditor();
                        quill.deleteText(quill.getText().length - (deltas != "" ? deltas?.length + 1 : 0), quill.getText().length - 1)
                    }
                }}
            />
        </div>
    )
};

const MultichoiceOption = ({ options, answer, onChange }) => {

    const onChangeMultiChoiceOption = (value) => {
        let arr = [...answer];

        let val = Number(value);
        let index = arr.indexOf(val);

        if (index > -1) {
            arr.splice(index, 1);
        } else {
            arr = [...arr, val];
        }
        onChange && onChange(arr.sort());
    };

    return (
        <Form className="flex-grow-1 px-4 px-sm-5">
            {
                options.map((item, index) => (
                    <div key={"checkbox-" + item.id} className="d-flex align-items-center justify-content-between my-1 gap-2">
                        <label className="label">{String.fromCharCode(97 + index)}{") "} {item.optionname}</label>
                        <Form.Check key={"checkbox-" + item.id} type="checkbox" id={item.id} value={item.id} defaultChecked={answer.includes(item.id) ? true : false}
                            onChange={(event) => onChangeMultiChoiceOption(event.target.value)} />
                    </div>
                ))
            }
        </Form>
    )
};

const SingleChoiceOption = ({ question, options, answer, onChange }) => {
    return (
        <Form className="flex-grow-1 px-4 px-sm-5">
            {
                options.map((item) => (
                    <div key={'radio-' + item.id} className="d-flex align-items-center justify-content-between my-1 gap-2">
                        <label className="label">{item.optionname}</label>
                        <Form.Check key={"radio-" + item.id} type="radio" name={"question-" + question.id} id={item.id} value={item.id} defaultChecked={answer.includes(item.id) ? true : false}
                            onChange={(event) => {
                                onChange([Number(event.target.value)]);
                            }} />
                    </div>
                ))
            }
        </Form>
    )
};

const ReorderOption = ({ options, answer, onChange }) => {
    return (
        <Draggable className="d-flex flex-wrap my-2 flex-column"
            onPosChange={(i, j) => {
                let ans = [...answer];
                const x = ans[i];
                ans[i] = ans[j];
                ans[j] = x;
                onChange(ans)
            }}>
            {
                options.map((item, index) => (
                    <div className="my-1 d-flex justify-content-between p-2 border bprder-1">
                        <label>{item?.optionname}</label>
                    </div>
                ))
            }
        </Draggable>
    )
};

const RatingOption = ({ answer, onChange }) => {
    return (
        <div className="flex-grow-1 px-4 px-sm-5">
            <Rating name="size-large"
                precision={0.5}
                value={answer}
                icon={<StarIcon fontSize="large" />}
                emptyIcon={<StarIcon fontSize="large" />}
                onChange={(event, newValue) => onChange(newValue)} />
        </div>
    )
};

const CodingOption = ({ question, answer, language, onChange }) => {
    return (
        <CodeIDE
            value={answer}
            showLanguageOption={question.programminglanguage == 'Any language'}
            onChange={(value, language) => onChange(value, language)}
            codeLanguage={language}
        />
    )
};